import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const RenaultCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation cover worth it for Renault Kiger?",
      answer:
        "Yes, especially in the first few years, to get full claim value on replaced parts.",
    },
    {
      question: "What type of insurance is best for Renault Kwid?",
      answer:
        "A comprehensive policy with zero depreciation and engine cover is recommended for complete protection.",
    },
    {
      question: "Can I buy insurance for my Renault online?",
      answer:
        "Yes, almost all insurers offer online purchase and renewal with minimal paperwork.",
    },
    {
      question: "Is the premium higher for turbocharged Kiger variants?",
      answer:
        "Slightly higher due to increased engine value, but well within budget range.",
    },
    {
      question: "Can I transfer my existing NCB to a new Renault Triber?",
      answer:
        "Yes, No Claim Bonus is transferrable between vehicles registered in your name.",
    },
    {
      question: "Does comprehensive insurance cover fire and theft?",
      answer:
        "Yes, it includes natural disasters, fire, theft, and third-party damages.",
    },
    {
      question: "How long does a Renault insurance claim take to settle?",
      answer:
        "Most claims are processed within 7 to 10 working days depending on inspection and paperwork.",
    },
    {
      question:
        "Can I add more than one driver to my Renault insurance policy?",
      answer:
        "Yes, you can include additional named drivers such as family members.",
    },
    {
      question: "What happens if I sell my Renault before policy expiry?",
      answer:
        "You can either transfer the policy or cancel it and request a refund of unused premium.",
    },
    {
      question: "Do I need engine cover for Renault Kwid?",
      answer: "Yes, particularly if you live in a flood-prone or coastal area.",
    },
    {
      question: "Will accessories like music systems be covered?",
      answer: "Yes, if declared and included in the sum insured.",
    },
    {
      question: "Does fuel type affect premium for Renault cars?",
      answer:
        "Diesel variants usually carry slightly higher premiums than petrol ones.",
    },
    {
      question: "Is third-party insurance enough for Renault cars?",
      answer:
        "It is mandatory but limited in coverage. A comprehensive plan is strongly recommended.",
    },
    {
      question: "Can I switch to a different insurer after one year?",
      answer: "Yes, you can change your insurer at the time of renewal.",
    },
    {
      question: "Is IDV the same as car market value?",
      answer:
        "Not exactly. IDV is the maximum sum insured your insurer will pay for total loss or theft.",
    },
    {
      question: "Is Return to Invoice available for all Renault models?",
      answer:
        "Yes, but usually only within the first 2 to 3 years of ownership.",
    },
    {
      question: "Will insurance cover tyre or rim damage?",
      answer: "Only if caused due to an accident and included in the claim.",
    },
    {
      question: "Is Renault insurance more affordable than other brands?",
      answer:
        "Generally, yes. Renault vehicles are budget-friendly and thus attract lower premiums.",
    },
    {
      question: "Can I renew my Renault policy after expiry?",
      answer:
        "Yes, within a grace period. However, delays may require inspection and loss of NCB.",
    },
    {
      question: "Are cashless garages available for Renault?",
      answer:
        "Yes, insurers have tie-ups with Renault-authorized service centers for cashless repairs.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Ensures full value for parts replacement without depreciation.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection",
      description: "Helps during flood-related damage or oil leakage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Provides help during sudden breakdowns, towing, or battery failure.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Covers total loss or theft with full invoice value.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Protects small parts such as brake fluid, nuts, bolts, and lubricants.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const renaultinsurance = [
    {
      name: 'Kwid',
      path: '/renault-kwid-car-insurance',
      image: '/assets/renaultkwid.jpeg',
    },
    {
      name: 'Kiger',
      path: '/renault-kiger-car-insurance',
      image: '/assets/renaultkiger.jpeg',
    },
    {
      name: 'Triber',
      path: '/renault-triber-car-insurance',
      image: '/assets/renaulttriber.jpeg',
    },
    {
      name: 'Duster',
      path: '/renault-duster-car-insurance',
      image: '/assets/renaultduster.jpeg',
    },
       
  ];
  return (
    <>
      <Helmet>
        <title>
          Renault Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Renault car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Renault vehicle."
        />
        <meta
          name="keywords"
          content="renault car insurance, auto insurance india, renault kwid insurance, renault triber insurance, renault kiger insurance, car insurance quotes"
        />
           <link
          rel="canonical"
          href="https://thefinmaster.com/renault-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Renault Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Renault Car Insurance 
                  </span>{" "}
                 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Renault has carved its niche in India with stylish designs, practicality, and value-for-money offerings. Whether you drive a compact Kwid or a versatile Triber, keeping your car secure begins with the right insurance. This guide takes you through everything you need to know about insuring a Renault vehicle, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/renaultinsurance.jpeg"
                alt="Renault Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Renault in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Renault India has introduced compact and practical vehicles that
            cater to a wide range of Indian car buyers. With bold styling and
            value-driven pricing, its lineup appeals to first-time buyers and
            small families alike.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Renault’s current portfolio includes: Renault Kwid: Entry-level
            hatchback with SUV-inspired styling, Renault Triber: Compact
            7-seater ideal for families, Renault Kiger: Stylish compact SUV with
            turbo engine options. All three models are designed for urban use
            and offer good fuel efficiency, spacious cabins, and innovative
            tech. To protect your Renault vehicle from unexpected damages and
            financial liability, the right insurance is a must.
          </p>
        </div>
      </div>
             <div className="p-4 sm:p-8 bg-white">
                                          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                            Choose Your Car Brand
                                          </h2>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                      {renaultinsurance.map((calc, index) => (
                                        <motion.div
                                          key={index}
                                          whileHover={{ scale: 1.04 }}
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ duration: 0.3, delay: index * 0.05 }}
                                        >
                                          <Link
                                            to={calc.path}
                                            className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                          >
                                            <img
                                              src={calc.image}
                                              alt={calc.name}
                                              className="w-16 h-16 object-contain mb-2"
                                            />
                                            <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                              {calc.name}
                                            </span>
                                          </Link>
                                        </motion.div>
                                      ))}
                                    </div>
                                    </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Renault Owners Need Good Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Even though Renault vehicles are budget-friendly, damages from
            accidents or natural disasters can lead to expensive repairs. A
            strong insurance policy gives you protection from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Collision-related repairs or body damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, vandalism, and fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Floods, cyclones, and other natural disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party injury or property damage claims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                Mechanical damage due to engine failure (with add-ons)
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            With an active policy, you also get access to Renault’s authorized
            garages, cashless repair service, and claim support when you need it
            most.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Renault Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here is a look at Renault’s key models along with their estimated
          Insured Declared Value (IDV) and starting comprehensive insurance
          premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Renault Kwid",
                category: "Entry Hatchback",
                idv: "4–6 Lakh",
                premium: "₹2,900 to ₹4,200",
              },
              {
                model: "Renault Triber",
                category: "MPV (7-Seater)",
                idv: "6–8.5 Lakh",
                premium: "₹3,500 to ₹5,500",
              },
              {
                model: "Renault Kiger",
                category: "Compact SUV",
                idv: "6.5–10 Lakh",
                premium: "₹3,800 to ₹6,200",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Renault Kwid",
                    "Entry Hatchback",
                    "4–6 Lakh",
                    "₹2,900 to ₹4,200",
                  ],
                  [
                    "Renault Triber",
                    "MPV (7-Seater)",
                    "6–8.5 Lakh",
                    "₹3,500 to ₹5,500",
                  ],
                  [
                    "Renault Kiger",
                    "Compact SUV",
                    "6.5–10 Lakh",
                    "₹3,800 to ₹6,200",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Renault Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Renault vehicles benefit from simple but effective engineering. These
          optional covers can boost your policy protection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Renault Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here are some top-rated insurers that offer competitive policies for
          Renault vehicles:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for online claim processing and add-on flexibility.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers strong network garage support and digital policy
              management.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Customizable plans with engine cover and depreciation shield.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              SBI General Insurance
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Budget-friendly premiums and wide acceptance.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Quick approvals and good NCB benefits.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Renault Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Follow these practical steps to reduce your premium:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes across multiple insurers.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain a clean driving record and claim-free history.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Choose voluntary deductibles if you drive occasionally.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install safety and anti-theft devices.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew early to avoid inspection and missed NCB discounts.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Renault Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              When buying or renewing insurance for your Renault, make sure you
              have:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy copy (for renewals)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Personal ID proof (Aadhaar, PAN, etc.)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Recent passport photo (for offline applications)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Renault Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Renault offers practical, smartly-designed cars tailored for
                Indian roads and budgets. Whether you're commuting daily in a
                Kwid, planning weekend getaways in a Triber, or cruising in a
                turbocharged Kiger, insuring your car with the right coverage is
                essential.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                With the right policy, you can drive worry-free and avoid
                unexpected repair expenses. TheFinMaster is here to help you
                compare options, understand your coverage, and get value-driven
                insurance for your Renault.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default RenaultCarInsurance;
